/**
 * Klien Admin API.
 *
 * Semua permintaan memakai path relatif `/admin/v1/...` di origin yang sama
 * (proxy Vite saat development, nginx di produksi), sehingga cookie sesi tidak
 * memerlukan CORS. Token CSRF disimpan di memori dan dikirim sebagai header
 * `X-TahuPay-CSRF` pada setiap permintaan yang mengubah data.
 */

export const API_BASE = '/admin/v1'

let sessionGeneration = 0
export function invalidateSessionRequests(): void {
  sessionGeneration += 1
}

function ensureSessionGeneration(generation: number): void {
  if (generation !== sessionGeneration) throw new DOMException('Sesi berubah', 'AbortError')
}

export type FieldError = {
  field: string
  code: string
  message: string
}

type ErrorEnvelope = {
  error?: {
    code?: string
    message?: string
    fields?: FieldError[]
    locked_until?: string
  }
  request_id?: string
}

/**
 * Error Admin API. Membawa status HTTP, kode dari server, pesan siap tampil,
 * daftar error per field, dan request id untuk penelusuran log.
 */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly fields: FieldError[]
  readonly requestId: string
  readonly lockedUntil: string | null
  /** Isi header Retry-After dalam detik, ada pada 429. */
  readonly retryAfter: number | null

  constructor(init: {
    status: number
    code: string
    message: string
    fields?: FieldError[]
    requestId?: string
    lockedUntil?: string | null
    retryAfter?: number | null
  }) {
    super(init.message)
    this.name = 'ApiError'
    this.status = init.status
    this.code = init.code
    this.fields = init.fields ?? []
    this.requestId = init.requestId ?? ''
    this.lockedUntil = init.lockedUntil ?? null
    this.retryAfter = init.retryAfter ?? null
  }
}

/** Pesan untuk field tertentu dari respons 422, kosong bila tidak ada. */
export function fieldMessage(error: unknown, field: string): string {
  if (!(error instanceof ApiError)) {
    return ''
  }
  return error.fields.find((item) => item.field === field)?.message ?? ''
}

let csrfToken = ''
let unauthorizedHandler: (() => void) | null = null

/** Menyimpan token CSRF di memori. Dipanggil setelah login dan `/auth/me`. */
export function setCsrfToken(token: string): void {
  csrfToken = token
}

/** Membaca token CSRF yang sedang berlaku. Dipakai uji dan pengiriman header. */
export function getCsrfToken(): string {
  return csrfToken
}

/**
 * Mendaftarkan penanganan 401. Konsol memakai ini untuk mengarahkan operator ke
 * halaman login sambil mengingat halaman asal.
 */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler
}

export function notifyUnauthorized(): void {
  csrfToken = ''
  unauthorizedHandler?.()
}

/** URL login yang membawa halaman asal, supaya operator kembali setelah masuk. */
export function loginPathFor(location: { pathname: string; search: string }): string {
  const target = `${location.pathname}${location.search}`
  if (!target || target === '/login') {
    return '/login'
  }
  return `/login?next=${encodeURIComponent(target)}`
}

/** Halaman asal dari parameter `next`, hanya untuk path internal. */
export function safeNextPath(raw: string | null): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) {
    return '/'
  }
  return raw
}

type QueryValue = string | number | boolean | undefined | null | string[]

export function toQueryString(query?: Record<string, QueryValue>): string {
  if (!query) {
    return ''
  }
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '' || value === false) {
      continue
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        continue
      }
      params.set(key, value.join(','))
      continue
    }
    params.set(key, String(value))
  }
  const text = params.toString()
  return text ? `?${text}` : ''
}

export type RequestOptions = {
  method?: string
  body?: unknown
  query?: Record<string, QueryValue>
  signal?: AbortSignal
}

/** Permintaan JSON ke Admin API. Melempar `ApiError` untuk respons bukan 2xx. */
export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const generation = sessionGeneration
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = { accept: 'application/json' }
  const sendsBody = options.body !== undefined

  if (sendsBody) {
    headers['content-type'] = 'application/json'
  }
  if (method !== 'GET' && method !== 'HEAD') {
    headers['X-TahuPay-CSRF'] = csrfToken
  }

  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}${toQueryString(options.query)}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: sendsBody ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error
    }
    throw new ApiError({
      status: 0,
      code: 'network_error',
      message: 'Tidak bisa menghubungi server admin.',
    })
  }

  ensureSessionGeneration(generation)
  if (response.status === 204) {
    return undefined as T
  }

  const payload = await readJson(response)
  ensureSessionGeneration(generation)

  if (!response.ok) {
    const envelope = (payload ?? {}) as ErrorEnvelope
    const status = response.status
    const error = new ApiError({
      status,
      code: envelope.error?.code ?? fallbackCode(status),
      message: envelope.error?.message ?? fallbackMessage(status),
      fields: envelope.error?.fields ?? [],
      requestId: envelope.request_id ?? '',
      lockedUntil: envelope.error?.locked_until ?? null,
      retryAfter: parseRetryAfter(response.headers.get('retry-after')),
    })
    if (status === 401) {
      notifyUnauthorized()
    }
    throw error
  }

  return payload as T
}

/** Detik dari header Retry-After, menerima bentuk angka maupun tanggal HTTP. */
function parseRetryAfter(header: string | null): number | null {
  if (!header) {
    return null
  }
  const seconds = Number(header)
  if (Number.isFinite(seconds)) {
    return Math.max(0, Math.ceil(seconds))
  }
  const date = Date.parse(header)
  if (Number.isNaN(date)) {
    return null
  }
  return Math.max(0, Math.ceil((date - Date.now()) / 1000))
}

async function readJson(response: Response): Promise<unknown> {
  const text = await response.text()
  if (!text) {
    return undefined
  }
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}

function fallbackCode(status: number): string {
  if (status === 401) return 'unauthenticated'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'not_found'
  if (status >= 500) return 'internal'
  return 'bad_request'
}

function fallbackMessage(status: number): string {
  if (status === 401) return 'Sesi tidak berlaku lagi. Masuk kembali untuk melanjutkan.'
  if (status === 403) return 'Akun ini tidak berhak menjalankan aksi tersebut.'
  if (status === 404) return 'Data yang diminta tidak ditemukan.'
  if (status >= 500) return 'Server admin gagal memproses permintaan.'
  return `Permintaan ditolak dengan kode ${status}.`
}

export type DownloadResult = {
  blob: Blob
  filename: string
}

/** Nama berkas dari Content-Disposition, dengan nilai cadangan bila tidak ada. */
export function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) {
    return fallback
  }
  const quoted = /filename="([^"]+)"/i.exec(header)
  if (quoted?.[1]) {
    return quoted[1]
  }
  const bare = /filename=([^;]+)/i.exec(header)
  if (bare?.[1]) {
    return bare[1].trim()
  }
  return fallback
}

/** Mengunduh berkas dari endpoint yang membalas lampiran, misalnya ekspor CSV. */
export async function downloadFile(
  path: string,
  query: Record<string, QueryValue>,
  fallbackName: string,
): Promise<DownloadResult> {
  const generation = sessionGeneration
  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}${toQueryString(query)}`, {
      headers: { accept: 'text/csv' },
      credentials: 'same-origin',
    })
  } catch {
    throw new ApiError({
      status: 0,
      code: 'network_error',
      message: 'Tidak bisa menghubungi server admin.',
    })
  }

  if (!response.ok) {
    const payload = (await readJson(response)) as ErrorEnvelope | undefined
    const status = response.status
    const error = new ApiError({
      status,
      code: payload?.error?.code ?? fallbackCode(status),
      message: payload?.error?.message ?? fallbackMessage(status),
      fields: payload?.error?.fields ?? [],
      requestId: payload?.request_id ?? '',
    })
    if (status === 401) {
      notifyUnauthorized()
    }
    throw error
  }

  const bytes = await response.arrayBuffer().catch(() => {
    throw new ApiError({ status: 0, code: 'export_incomplete', message: 'Unduhan CSV terputus. Coba ekspor lagi.' })
  })
  ensureSessionGeneration(generation)
  const length = response.headers.get('content-length')
  const expectedHash = response.headers.get('x-export-sha256')
  if (!length || !/^\d+$/.test(length) || Number(length) !== bytes.byteLength || !expectedHash || !/^[a-f0-9]{64}$/.test(expectedHash)) {
    throw new ApiError({ status: 0, code: 'export_incomplete', message: 'CSV tidak lengkap atau bukti kelengkapannya tidak tersedia. Coba ekspor lagi.' })
  }
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  const hash = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('')
  ensureSessionGeneration(generation)
  if (hash !== expectedHash) throw new ApiError({ status: 0, code: 'export_incomplete', message: 'Isi CSV tidak cocok dengan hasil server. Coba ekspor lagi.' })
  return {
    blob: new Blob([bytes], { type: 'text/csv;charset=utf-8' }),
    filename: filenameFromDisposition(response.headers.get('content-disposition'), fallbackName),
  }
}
